import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { AnimatePresence, motion } from 'framer-motion';
import { DashboardLayout } from '@/components/layout/MapLayout';
import { SidebarRef } from '@/components/layout/Sidebar';
import { MobileTabNavigation, MobileTab } from '@/components/layout/MobileTabNavigation';
import { MobileUnitList } from '@/components/layout/MobileUnitList';
import { MapView } from '@/components/map/MapView';
import { UnitDetailView } from '@/components/unit/UnitDetailView';
import { GalleryPage } from './GalleryPage';
import { getUnits, fetchUnitLocations, updateHVAC, fetchUnitById } from '@/lib/api';
import { useUnitStatus } from '@/contexts/UnitStatusContext';
import { useFilters } from '@/contexts/FilterContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useUrlState } from '@/hooks/useUrlState';
import { useHierarchicalSearch } from '@/hooks/useHierarchicalSearch';
import { matchesAlertFilter, type AlertFilterState } from '@/lib/unitFilters';
import { updateUnitLocations } from '@/lib/locationDiff';
import { updateUnitStatuses } from '@/lib/statusDiff';
import { useRBAC } from '@/contexts/RBACContext';
import { useMap } from '@/contexts/MapContext';
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import {
  BOUNDS_PADDING,
  ZOOM_THRESHOLDS,
  isValidLocation,
  calculateWeightedCentroidBounds,
} from '@/components/map/mapConfig';
import type { Unit, ViewMode } from '@/types';
import { LottieLoading } from '@/components/ui/lottie-loading';
import { MapSlowNotice } from '@/components/map/MapSlowNotice';

/** The longest the loading overlay may stay up (ISS-136). */
const LOADING_CEILING_MS = 10_000;
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { trackUnitSelected, trackMapSearch, trackHvacUpdated, trackMapViewModeChanged } from '@/lib/analytics';

// Placeholder unit for UnitDetailView when no unit selected (maintains hook count)
const PLACEHOLDER_UNIT: Unit = {
  id: '',
  unit_code: '',
  name: '',
  starlinkId: '',
  status: 'offline',
  location: { lat: 0, lng: 0, address: '' },
  image: '',
  lastUpdate: new Date().toISOString(),
  company_id: '',
  primary_group_id: null,
};

/**
 * Render the interactive unit dashboard that presents a map, sidebar, list, detail panel, and gallery for viewing and managing units.
 *
 * The component manages filtering, selection, hover state, per-unit tabs, URL state synchronization, and periodic background syncing of unit statuses and locations. It adapts layout and navigation for mobile vs. desktop, preserves the currently selected unit across updates, and exposes actions for refreshing data and performing system updates (e.g., HVAC).
 *
 * @returns The dashboard page UI for viewing and interacting with the unit operations.
 */
export function MapPage() {
  const isMobile = useIsMobile();
  const sidebarRef = useRef<SidebarRef | null>(null);
  const mountedRef = useRef(true);
  const hasRestoredFromUrl = useRef(false); // Track if we've restored state from URL
  const { selectedCompanyIds, selectedGroupIds, selectedIndustryTypes, selectedModels, selectedStatuses, setSelectedStatuses, companies, groups, setSelectedCompanyIds, setSelectedGroupIds, setSelectedModels, setSelectedIndustryTypes, flushCache, setUnits: setContextUnits, hasSavedFilters, restoreSavedFilters } = useFilters();
  const { getInitialState, updateUrlState } = useUrlState();
  const { isAdmin, isInternal } = useRBAC();
  const { map } = useMap();
  const { statuses: contextStatuses } = useUnitStatus();

  // Apply mobile scroll lock to prevent body scrolling while on map page
  // This allows map panning without the page bouncing
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('mobile-scroll-lock');
    }
    return () => {
      document.body.classList.remove('mobile-scroll-lock');
    };
  }, [isMobile]);

  // Initialize state from URL (or defaults)
  const currentUrlState = getInitialState();
  const initialUrlState = useMemo(() => currentUrlState, [
    currentUrlState.unitSlug,
    currentUrlState.selectedUnitName,
    currentUrlState.selectedTab,
    currentUrlState.viewMode,
    currentUrlState.mobileTab,
    currentUrlState.searchQuery,
    currentUrlState.showAlerts,
    currentUrlState.viewport,
    // Stable representation of Set for dependency comparison
    currentUrlState.statusFilters ? Array.from(currentUrlState.statusFilters).sort().join(',') : null
  ]);
  
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  // Full detail data for the selected unit (fetched on demand). The lean /units list
  // omits Starlink metricsHistory + wifiClients; we fetch them here for the detail panel.
  const [detailUnit, setDetailUnit] = useState<Unit | null>(null);
  const [hoveredUnit, setHoveredUnit] = useState<Unit | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(initialUrlState.viewMode || 'map');
  const [mobileTab, setMobileTab] = useState<MobileTab>(initialUrlState.mobileTab || 'map');
  const [searchQuery, setSearchQuery] = useState(initialUrlState.searchQuery || '');
  // Use hierarchical search for company/group expansion
  const { matchesUnit } = useHierarchicalSearch(searchQuery);
  // Convert FilterContext's selectedStatuses (string[]) to Set for StatusBar compatibility
  const statusFilters = useMemo(() => new Set(selectedStatuses as ('online' | 'offline' | 'idle' | 'unknown')[]), [selectedStatuses]);
  const [showAlerts, setShowAlerts] = useState<AlertFilterState>(initialUrlState.showAlerts || 'none');
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [minLoadingComplete, setMinLoadingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Track active tab per unit ID - each unit remembers its own tab
  const [unitTabs, setUnitTabs] = useState<Map<string, 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi'>>(new Map());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewportBounds, setViewportBounds] = useState<{ ne: [number, number]; sw: [number, number] } | null>(null);
  const [viewportFilterEnabled, setViewportFilterEnabled] = useState(() => {
    // Load from URL first, then localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlValue = urlParams.get('viewport');
    if (urlValue !== null) {
      return urlValue === 'true';
    }
    // Fallback to localStorage
    const saved = localStorage.getItem('viewportFilterEnabled');
    return saved !== 'false'; // Default to true
  });
  
  // Track source of hover to prevent scroll loops
  const hoverSource = useRef<'map' | 'sidebar' | null>(null);
  
  // Track previous mobile tab to support correct "Back" navigation
  const [previousMobileTab, setPreviousMobileTab] = useState<MobileTab>('map');
  const [scrollToTabs, setScrollToTabs] = useState(false);

  // Handle map hover - updates source to 'map'
  // CRITICAL: Use functional setState to avoid hoveredUnit dependency.
  // If hoveredUnit is a dependency, every hover change recreates this callback,
  // triggering useMapEvents effect re-runs which break Mapbox hover detection.
  const handleMapHover = useCallback((unit: Unit | null) => {
    setHoveredUnit(prev => {
      // Only update if different
      if (unit?.id !== prev?.id) {
        hoverSource.current = 'map';
        return unit;
      } else if (!unit && prev) {
        // Handle clearing hover
        hoverSource.current = 'map';
        return null;
      }
      return prev; // No change
    });
  }, []); // Stable callback - no dependencies

  // Handle sidebar hover - updates source to 'sidebar'
  // Same pattern as handleMapHover for stability
  const handleSidebarHover = useCallback((unit: Unit | null) => {
    setHoveredUnit(prev => {
      // Only update if different
      if (unit?.id !== prev?.id) {
        hoverSource.current = 'sidebar';
        return unit;
      } else if (!unit && prev) {
        // Handle clearing hover
        hoverSource.current = 'sidebar';
        return null;
      }
      return prev; // No change
    });
  }, []); // Stable callback - no dependencies

  // Auto-scroll sidebar when hovering map (desktop only)
  useEffect(() => {
    // Only scroll if:
    // 1. We have a hovered unit
    // 2. The hover originated from the map (not the sidebar itself)
    // 3. We are on desktop
    // 4. Sidebar is not collapsed
    if (hoveredUnit && hoverSource.current === 'map' && !isMobile && !isSidebarCollapsed) {
      sidebarRef.current?.scrollToUnit(hoveredUnit.id);
    }
  }, [hoveredUnit, isMobile, isSidebarCollapsed]);

  // Restore selected unit and view mode from URL after units are loaded
  // Also handles external navigation (e.g. browser back/forward or deep links from UI)
  useEffect(() => {
    if (units.length === 0) return;

    // Helper to compare if we need to update state
    // This prevents infinite loops by only updating when state effectively changes
    const shouldUpdate = () => {
      // Check unit mismatch
      let targetUnit = null;
      
      if (initialUrlState.unitSlug) {
        const slug = initialUrlState.unitSlug;
        targetUnit = units.find(t => t.unit_code === slug || t.id === slug) || 
                      units.find(t => slug.endsWith(t.unit_code));
      } else if (initialUrlState.selectedUnitName) {
        targetUnit = units.find(t => t.name === initialUrlState.selectedUnitName);
      }

      if (targetUnit && targetUnit.id !== selectedUnit?.id) return true;
      if (!targetUnit && selectedUnit) return true; // URL cleared but unit selected

      // Check tab mismatch (only if unit matches)
      if (targetUnit && initialUrlState.selectedTab) {
        const currentTab = unitTabs.get(targetUnit.id);
        // Always update if URL has a tab but unit doesn't have one set yet
        // This fixes the case where clicking a system icon from a tooltip doesn't select the tab
        if (!currentTab && initialUrlState.selectedTab) return true;
        // Update if tabs don't match
        if (currentTab && currentTab !== initialUrlState.selectedTab) return true;
      }

      // Check view/tab mismatch
      if (isMobile) {
        const targetMobileTab = initialUrlState.mobileTab || (targetUnit ? 'detail' : 'map');
        if (targetMobileTab !== mobileTab) return true;
      } else {
        const targetViewMode = initialUrlState.viewMode || (targetUnit ? 'detail' : 'map');
        if (targetViewMode !== viewMode) return true;
      }

      return false;
    };

    if (!shouldUpdate() && hasRestoredFromUrl.current) return;

    // Perform updates
    if (initialUrlState.unitSlug) {
      const slug = initialUrlState.unitSlug.toLowerCase(); // Normalize input slug
      console.log(`[Dashboard] Restoring from slug: "${slug}"`);

      // Try to find unit by matching slug pattern
      // Case-insensitive matching for unit_code
      let unit = units.find(t => 
        t.unit_code.toLowerCase() === slug || 
        t.id.toLowerCase() === slug
      );
      
      if (!unit) {
        // Match if slug ends with unit_code (case-insensitive)
        unit = units.find(t => slug.endsWith(t.unit_code.toLowerCase()));
      }

      console.log(`[Dashboard] Unit found for restoration? ${unit ? 'YES' : 'NO'} (ID: ${unit?.id})`);
      
      if (unit) {
        if (selectedUnit?.id !== unit.id) setSelectedUnit(unit);
        
        // Restore tab
        if (initialUrlState.selectedTab) {
          setUnitTabs(prev => {
            const current = prev.get(unit!.id);
            if (current === initialUrlState.selectedTab) return prev;
            
            const newMap = new Map(prev);
            newMap.set(unit!.id, initialUrlState.selectedTab!);
            return newMap;
          });
          setScrollToTabs(true);
        } else {
          setScrollToTabs(false);
        }
        // Restore view mode
        // If we found a unit, we should be in detail view unless explicitly gallery
        const targetViewMode = initialUrlState.viewMode === 'gallery' ? 'gallery' : 'detail';
        
        if (targetViewMode !== viewMode) {
          setViewMode(targetViewMode);
        }
        
        // CRITICAL: Ensure mobile tab is synced to detail if on mobile
        // But respect explicit mobile tab from URL (prevents loops when going back)
        if (isMobile && mobileTab !== 'detail' && !initialUrlState.mobileTab) {
          setPreviousMobileTab(mobileTab);
          setMobileTab('detail');
        }
      }
    } else if (initialUrlState.selectedUnitName) {
      // Legacy fallback
      const unit = units.find(t => t.name === initialUrlState.selectedUnitName);
      if (unit) {
        if (selectedUnit?.id !== unit.id) setSelectedUnit(unit);
        
        if (initialUrlState.selectedTab) {
          setUnitTabs(prev => {
            const current = prev.get(unit!.id);
            if (current === initialUrlState.selectedTab) return prev;

            const newMap = new Map(prev);
            newMap.set(unit.id, initialUrlState.selectedTab!);
            return newMap;
          });
          setScrollToTabs(true);
        } else {
          setScrollToTabs(false);
        }
        // Restore view mode
        const targetViewMode = initialUrlState.viewMode === 'gallery' ? 'gallery' : 'detail';
        if (targetViewMode !== viewMode) {
          setViewMode(targetViewMode);
        }
        
        // CRITICAL: Ensure mobile tab is synced to detail if on mobile
        // But respect explicit mobile tab from URL (prevents loops when going back)
        if (isMobile && mobileTab !== 'detail' && !initialUrlState.mobileTab) {
          setPreviousMobileTab(mobileTab);
          setMobileTab('detail');
        }
      }
    } else {
      // If URL has no unit, ensure we deselect if currently selected
      // ONLY if we are not in the initial restoration phase (to avoid clearing state if URL is just empty on load)
      // Actually, if URL is empty, we SHOULD be in map mode.
      // But carefully: if user navigates to /, we want to clear selection.
      // We rely on checking if 'unit' params are missing.
      if (selectedUnit && hasRestoredFromUrl.current) {
        // Only clear if params are explicitly missing
        if (!initialUrlState.unitSlug && !initialUrlState.selectedUnitName) {
           setSelectedUnit(null);
           setViewMode('map');
        }
      }
    }

    // Mark restoration as complete
    hasRestoredFromUrl.current = true;
  }, [units, initialUrlState]); // Re-run when units load OR URL state changes

  // Sync state changes to URL (only after initial restoration is complete)
  useEffect(() => {
    // Skip URL sync until we've completed the initial restoration from URL
    // This prevents clearing URL params before units are loaded
    if (!hasRestoredFromUrl.current) return;

    // Get current unit's tab for URL sync
    // IMPORTANT: Prefer unitTabs state, but fall back to URL's tab value if unitTabs
    // hasn't been set yet for this unit. This prevents a race condition where navigation
    // sets tab=hvac in URL, but state-to-URL sync overwrites it with 'solar' default
    // before unitTabs has been updated from the URL-to-state sync.
    const currentTab = selectedUnit
      ? (unitTabs.get(selectedUnit.id) || initialUrlState.selectedTab || 'solar')
      : undefined;
    
    // Helper to generate semantic slug
    // Format: [unit_code] (e.g. "1RE001TCA")
    const generateSlug = (unit: Unit) => {
      return unit.unit_code;
    };

    updateUrlState({
      selectedUnitName: selectedUnit?.name, // Keep legacy support if needed, but rely on slug
      unitSlug: selectedUnit ? generateSlug(selectedUnit) : undefined,
      selectedTab: currentTab, // Only include tab when unit is selected
      viewMode,
      searchQuery,
      statusFilters,
      showAlerts,
      mobileTab,
      viewport: viewportFilterEnabled ? 'true' : 'false',
    });
  }, [selectedUnit, unitTabs, viewMode, searchQuery, statusFilters, showAlerts, mobileTab, viewportFilterEnabled, updateUrlState, initialUrlState.selectedTab]);

  // Sync selectedUnit with latest data from units array
  // This ensures live updates (status/location) are reflected immediately in DetailView
  // without requiring a manual refresh or re-selection.
  // NOTE: We intentionally do NOT sync hoveredUnit here because:
  // 1. The tooltip only shows static info (image, name) that doesn't change on status updates
  // 2. Syncing hoveredUnit on every status poll (15s) would trigger useMapPopups effect re-run
  // 3. This disrupts the popup's event handlers causing premature tooltip dismissal
  useEffect(() => {
    if (selectedUnit) {
      const updatedSelected = units.find(t => t.id === selectedUnit.id);
      // Only update if the object reference has actually changed (which implies data update)
      if (updatedSelected && updatedSelected !== selectedUnit) {
        setSelectedUnit(updatedSelected);
      }
    }
  }, [units, selectedUnit]);

  // Fetch full detail data (incl. Starlink metricsHistory + wifiClients, which the
  // lean /units list omits) for the selected unit so the detail panel charts render.
  // Keyed on the unit id, not the object, so the 15s status poll doesn't refetch.
  // Status/battery stay live via the lean selectedUnit synced above.
  useEffect(() => {
    const id = selectedUnit?.id;
    // No selection: leave detailUnit as-is. detailUnitForView returns null when
    // selectedUnit is falsy, and only reuses detailUnit when ids match, so stale
    // detail can't leak — and we avoid a synchronous setState inside the effect.
    if (!id) return;
    let cancelled = false;
    const loadDetail = async () => {
      try {
        const full = await fetchUnitById(id);
        if (!cancelled) setDetailUnit(full);
      } catch {
        // keep lean unit; detail charts fall back to placeholders
      }
    };
    void loadDetail();
    return () => { cancelled = true; };
  }, [selectedUnit?.id]);

  // Graft the detail-only Starlink arrays from the fetched full unit onto the live
  // (lean) selectedUnit. Everything else stays live from the 15s status poll.
  const detailUnitForView = useMemo<Unit | null>(() => {
    if (!selectedUnit) return null;
    const full = detailUnit && detailUnit.id === selectedUnit.id ? detailUnit : null;
    if (!full || !selectedUnit.systems?.starlink) return selectedUnit;
    return {
      ...selectedUnit,
      systems: {
        ...selectedUnit.systems,
        starlink: {
          ...selectedUnit.systems.starlink,
          // Only the detail-only arrays are missing from the lean list; graft them in.
          // Client counts/summary fields are already present on the lean selectedUnit.
          metricsHistory: full.systems?.starlink?.metricsHistory,
          wifiClients: full.systems?.starlink?.wifiClients,
        },
      },
    };
  }, [selectedUnit, detailUnit]);

  // Sync statuses from UnitStatusContext into units state
  // The context handles polling every 15s - we just react to updates
  useEffect(() => {
    if (contextStatuses.size === 0 || units.length === 0) return;

    setUnits(currentUnits => {
      // Convert Map to Array format expected by updateUnitStatuses
      const statusArray = Array.from(contextStatuses.entries()).map(([id, info]) => ({
        id,
        status: info.status,
        lastUpdate: info.lastUpdate,
        warning_count: info.warning_count,
        fault_count: info.fault_count,
        live_status: info.live_status,
      }));

      const { units: updatedUnits, changedCount } = updateUnitStatuses(
        currentUnits,
        statusArray
      );

      if (changedCount > 0) {
        console.log(`📊 Updated ${changedCount} unit status(es) from context`);
      }

      return updatedUnits;
    });
  }, [contextStatuses]); // Re-run when context statuses update

  // Location polling and initial load
  // Location polling (hourly) - location updates only when needed
  // Full refresh (on-demand) - initial load or manual refresh
  useEffect(() => {
    mountedRef.current = true;
    loadUnits();

    // Location polling: Update locations hourly (trailers don't move frequently)
    // Locations are CACHED and only updated here or on manual refresh
    const locationPollInterval = setInterval(async () => {
      try {
        const { locations } = await fetchUnitLocations();

        setUnits(currentTrucks => {
          const { units: updatedUnits, changedCount } = updateUnitLocations(
            currentTrucks,
            locations
          );

          if (changedCount > 0) {
            console.log(`📍 Updated ${changedCount} unit location(s)`);
          }

          return updatedUnits;
        });
      } catch (error) {
        console.error('Failed to poll unit locations:', error);
      }
    }, 3600000); // 1 hour

    return () => {
      mountedRef.current = false;
      clearInterval(locationPollInterval);
    };
  }, []); // Only run on mount - no dependencies on filter state

  const loadUnits = async () => {
    // Skip loading if component is unmounting (during HMR)
    if (!mountedRef.current) return;

    // getUnits() serves cached units first for an instant map, then revalidates in
    // the background. Applying that revalidation is what lets a unit reassigned in
    // Settings move to its new production here without a reload — otherwise the
    // corrected list only reaches the cache, and the map keeps the stale one.
    const applyUnits = (data: Unit[]) => {
      if (!mountedRef.current) return;
      console.log(`[Dashboard] Loaded ${data.length} units from API`);
      if (data.length > 0) {
        console.log('[Dashboard] Sample unit IDs:', data.slice(0, 3).map(t => t.unit_code).join(', '));
      }

      setUnits(data);
      setContextUnits(data); // Push to FilterContext for global access
      setError(null);
    };

    try {
      // Load ALL units user has permission to see (RLS handles permissions on backend)
      // Frontend filtering happens in filteredUnits calculation
      applyUnits(await getUnits(undefined, { onRefresh: applyUnits }));
    } catch (err) {
      setError('Failed to load units');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh button click: flush cache and reload
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      flushCache(); // Clear filter cache
      await loadUnits(); // Reload unit data
    } finally {
      setIsRefreshing(false);
    }
  };

  // Ref to prevent auto-zoom re-triggering during animation (breaks infinite loop)
  const isAutoZoomingRef = useRef(false);

  // Filter units client-side (all filtering happens in frontend)
  // IMPORTANT: Memoized to prevent infinite loop with auto-zoom effect
  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      // CRITICAL: Always include the selected unit in the filtered list
      // This ensures it doesn't disappear from the map or cause mobile detail view to close
      if (selectedUnit?.id === unit.id) {
        return true;
      }

      const matchesStatus = statusFilters.has(unit.status);
      // Fault/warning filtering is staff only (Eric, 2026-11-02): customers get no counts and no
      // control, so an old ?alerts= link must not leave their map filtered to nothing.
      const matchesAlerts = matchesAlertFilter(unit, isInternal ? showAlerts : 'none');

      const query = searchQuery.toLowerCase();
      const modelPrefix = unit.unit_code.substring(0, 3);
      const modelLabel = MODEL_LABELS[modelPrefix] || '';

      // Use hierarchical search: matches company/group hierarchy + direct string matching
      // Also check model label for non-hierarchical model name searches
      const matchesSearch = matchesUnit(unit) ||
        (query && modelLabel.toLowerCase().includes(query));

      // Filter by selected industry types (NO industry types selected = NO data shown)
      // If industry_type is undefined, default to 'film' for filtering purposes
      const unitType = unit.industry_type || 'film';
      const matchesType = selectedIndustryTypes.length > 0 && selectedIndustryTypes.includes(unitType);

      // Filter by selected models (NO models selected = NO data shown)
      // Extract first 3 chars of unit_code for model matching
      // IMPORTANT: If no models are selected, hide all units
      const matchesModel = selectedModels.length > 0 && selectedModels.includes(modelPrefix);

      // Filter by selected companies (NO companies selected = NO data shown)
      const matchesCompany = selectedCompanyIds.length === 0
        ? false  // No companies selected = no matches
        : selectedCompanyIds.includes(unit.company_id) ||
          (selectedCompanyIds.includes('__unassigned__') && !unit.company_id);

      // Filter by selected groups (NO groups selected = NO data shown)
      const matchesGroup = selectedGroupIds.length === 0
        ? false  // No groups selected = no matches
        : (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
          (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);

      return matchesStatus && matchesAlerts && matchesSearch && matchesType && matchesModel && matchesCompany && matchesGroup;
    });
  }, [
    units,
    selectedUnit?.id,
    statusFilters,
    showAlerts,
    isInternal,
    searchQuery,
    matchesUnit,
    selectedIndustryTypes,
    selectedModels,
    selectedCompanyIds,
    selectedGroupIds
  ]);

  // Close mobile detail view when global filters change, BUT only if the unit is no longer visible
  // This handles interactions with the Company Dropdown and other filters in the StatusBar/Header
  useEffect(() => {
    // Skip on initial load/restoration
    if (!hasRestoredFromUrl.current) return;
    
    // Only run this logic if we are in mobile detail view with a selected unit
    if (isMobile && mobileTab === 'detail' && selectedUnit) {
       // Check if the currently selected unit is still in the filtered list
       // This prevents closing the view when filters change but the unit is still valid
       // (e.g. during initial load race conditions or benign filter changes)
       const isVisible = filteredUnits.some(t => t.id === selectedUnit.id);
       
       if (!isVisible) {
         setMobileTab(previousMobileTab);
         setSelectedUnit(null);
       }
    }
  }, [selectedCompanyIds, selectedGroupIds, selectedIndustryTypes, selectedModels, isMobile, mobileTab, selectedUnit, filteredUnits, previousMobileTab]);

  // Auto-zoom to search results when hierarchical search matches
  // Uses isAutoZoomingRef to prevent re-triggering during animation
  useEffect(() => {
    if (!searchQuery || !map || filteredUnits.length === 0) return;

    // Prevent re-zoom if we're already animating (breaks infinite loop)
    if (isAutoZoomingRef.current) return;

    // Only auto-zoom if we have search results with real GPS coordinates
    const unitsWithLocation = filteredUnits.filter(
      unit => unit.location?.lng && unit.location?.lat && !unit.location?.location_missing
    );

    if (unitsWithLocation.length === 0) return;

    // Set flag to prevent re-triggering during animation
    isAutoZoomingRef.current = true;

    // If only 1 result, zoom to it directly
    if (unitsWithLocation.length === 1) {
      const unit = unitsWithLocation[0];
      map.flyTo({
        center: [unit.location.lng, unit.location.lat],
        zoom: 14,
        duration: 300
      });
    } else {
      // Multiple results - fit bounds to show all matching units
      const bounds = new mapboxgl.LngLatBounds();
      unitsWithLocation.forEach(unit => {
        bounds.extend([unit.location.lng, unit.location.lat]);
      });

      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 12,
        duration: 300
      });
    }

    // Reset flag after animation completes (duration + buffer)
    const timer = setTimeout(() => {
      isAutoZoomingRef.current = false;
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, map, filteredUnits]);

  // Filter sidebar units by viewport bounds (bbox filtering) - only if enabled
  const sidebarUnits = viewportFilterEnabled && viewportBounds
    ? filteredUnits.filter((unit) => {
        const { lng, lat } = unit.location;
        return (
          lng >= viewportBounds.sw[0] &&
          lng <= viewportBounds.ne[0] &&
          lat >= viewportBounds.sw[1] &&
          lat <= viewportBounds.ne[1]
        );
      })
    : filteredUnits; // Show all filtered units if viewport filtering is disabled or no bounds yet

  const handleViewportChange = useCallback((bounds: mapboxgl.LngLatBounds) => {
    let swLng = bounds.getSouthWest().lng;
    let neLng = bounds.getNorthEast().lng;

    // At low zoom, viewport can span 360+ degrees with unwrapped longitudes.
    // Clamp to [-180, 180] so all unit coordinates (which are normalized) match.
    if (neLng - swLng >= 360) {
      swLng = -180;
      neLng = 180;
    } else {
      // Normalize wrapped longitudes back into a range that overlaps [-180, 180]
      while (swLng > 180) { swLng -= 360; neLng -= 360; }
      while (neLng < -180) { swLng += 360; neLng += 360; }
    }

    setViewportBounds({
      ne: [neLng, bounds.getNorthEast().lat],
      sw: [swLng, bounds.getSouthWest().lat],
    });
  }, []);

  // Restore View: restore saved filters AND fly map to show all units
  // This is called from both the sidebar button and should match the map's "Restore View" behavior
  const handleRestoreView = useCallback(() => {
    // First, restore the saved filter state (if any)
    if (hasSavedFilters) {
      restoreSavedFilters();
    }

    // Disable viewport filtering
    setViewportFilterEnabled(false);
    localStorage.setItem('viewportFilterEnabled', 'false');

    // Fly map to show all filtered units
    // Note: After restoreSavedFilters(), filteredUnits will update on next render
    // The map will then show the correct units after the filter state propagates
    if (map && filteredUnits.length > 0) {
      const unitsWithRealLocation = filteredUnits.filter(u => isValidLocation(u.location));
      if (unitsWithRealLocation.length > 0) {
        const coordinates = unitsWithRealLocation.map(u => ({ lat: u.location.lat, lng: u.location.lng }));
        const weightedBounds = calculateWeightedCentroidBounds(coordinates, 0.95);
        if (weightedBounds) {
          map.fitBounds(
            new mapboxgl.LngLatBounds(weightedBounds.sw, weightedBounds.ne),
            {
              padding: isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop,
              duration: 800,
              maxZoom: ZOOM_THRESHOLDS.MAX_AUTO_ZOOM,
            }
          );
        }
      }
    }
  }, [map, filteredUnits, isMobile, hasSavedFilters, restoreSavedFilters]);

  const handleSelectUnit = (unit: Unit | null, tab?: 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi') => {
    setSelectedUnit(unit);
    if (unit) trackUnitSelected(unit.id, unit.unit_code, 'list');

    // If unit is null, we're deselecting - return to map view
    if (!unit) {
      if (isMobile) {
        setMobileTab('map');
      } else {
        setViewMode('map');
      }
      return;
    }

    // Determine which tab to use:
    // 1. If tab is explicitly provided (from system icon click), use it
    // 2. If no tab provided (from unit name click), default to 'solar' (Reset behavior)
    let tabToUse: 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi' = 'solar';

    if (tab) {
      tabToUse = tab;
      setScrollToTabs(true);
    } else {
      // Explicitly reset to solar when clicking unit name, regardless of previous state
      tabToUse = 'solar';
      setScrollToTabs(false);
    }

    // Store the tab for this unit
    setUnitTabs(prev => {
      const newMap = new Map(prev);
      newMap.set(unit.id, tabToUse);
      return newMap;
    });

    if (isMobile) {
      setPreviousMobileTab(mobileTab === 'detail' ? 'map' : mobileTab);
      setMobileTab('detail');
    } else {
      setViewMode('detail');
    }
  };

  const handleMarkerClick = (unit: Unit | null) => {
    setSelectedUnit(unit);
    if (unit) {
      trackUnitSelected(unit.id, unit.unit_code, 'map');
      if (isMobile) {
        // On mobile, switch to detail tab
        setMobileTab('detail');
      } else {
        // On desktop, switch to detail view
        setViewMode('detail');
      }
    } else {
      // Deselecting - return to map view
      if (isMobile) {
        setMobileTab('map');
      } else {
        setViewMode('map');
      }
    }
  };

  const handleMobileTabChange = (tab: MobileTab) => {
    setMobileTab(tab);
    if (tab === 'map') {
      setViewMode('map');
    }
  };

  const handleBackToMap = () => {
    trackMapViewModeChanged('map');
    setViewMode('map');
    setSelectedUnit(null);
  };

  const handleOpenGallery = () => {
    trackMapViewModeChanged('gallery');
    setViewMode('gallery');
  };

  const handleSystemUpdate = async (unitId: string, system: string, settings: Record<string, unknown>) => {
    if (system === 'hvac') {
      try {
        await updateHVAC(unitId, settings as { targetTemp?: number; mode?: 'cool' | 'heat' | 'fan' | 'off'; fanSpeed?: 'low' | 'med' | 'high' });
        trackHvacUpdated(unitId);
        // Refresh unit data
        await loadUnits();
      } catch (err) {
        console.error('Failed to update HVAC:', err);
      }
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) trackMapSearch(query.length, filteredUnits.length);
    if (isMobile && mobileTab === 'detail') {
       setMobileTab(previousMobileTab);
       setSelectedUnit(null);
    }
  };

  const handleStatusFiltersChange = (filters: Set<'online' | 'offline' | 'idle' | 'unknown'>) => {
    // Convert Set to array for FilterContext
    setSelectedStatuses(Array.from(filters));
    if (isMobile && mobileTab === 'detail') {
       setMobileTab(previousMobileTab);
       setSelectedUnit(null);
    }
  };

  const handleShowAlertsChange = (show: AlertFilterState) => {
    setShowAlerts(show);
    if (isMobile && mobileTab === 'detail') {
       setMobileTab(previousMobileTab);
       setSelectedUnit(null);
    }
  };

  const handleClearFilters = () => {
    // Reset local Dashboard state
    setSearchQuery('');
    setSelectedStatuses(['online', 'offline', 'idle', 'unknown']);
    setShowAlerts('none');

    // Reset FilterContext state to defaults (all selected)
    setSelectedCompanyIds(companies.map((a) => a.id));
    setSelectedGroupIds(groups.map((g) => g.id));
    setSelectedModels(Object.keys(MODEL_LABELS));
    setSelectedIndustryTypes(INDUSTRY_TYPE_ORDER);

    if (isMobile && mobileTab === 'detail') {
       setMobileTab(previousMobileTab);
       setSelectedUnit(null);
    }
  };

  // Cheeky loading messages for the final stage (movie/sci-fi references)
  const quirkyMessages = [
    'Charging flux capacitor...',
    'Calibrating dilithium crystals...',
    'Reticulating splines...',
    'Phoning home...',
    'Warming up the DeLorean...',
    'Assembling adamantium rods...',
    'Bypassing the mainframe...',
    'Reversing the polarity...',
    'Initializing warp drive...',
    'Loading dark matter...',
    'Activating turbo encabulators...',
    'Syncing with the mothership...',
    'Powering up the arc reactor...',
    'Aligning quantum resonators...',
    'Brewing fresh coffee...',
  ];

  // Staged loading messages with specific timings (total 3.5s)
  // Phase 1: 1.4s, Phase 2: 1.4s, Quirky: 0.7s
  const [loadingPhase, setLoadingPhase] = useState(0);

  // Pick one random quirky message on mount
  const [quirkyMessage] = useState(() =>
    quirkyMessages[Math.floor(Math.random() * quirkyMessages.length)]
  );

  // Progress through loading phases (0.75s, 1s, 1s, then 0.5s transition)
  useEffect(() => {
    const phase1Timer = setTimeout(() => setLoadingPhase(1), 750);   // After 0.75s
    const phase2Timer = setTimeout(() => setLoadingPhase(2), 1750);  // After 1.75s
    const completeTimer = setTimeout(() => setMinLoadingComplete(true), 2750);  // After 2.75s

    return () => {
      clearTimeout(phase1Timer);
      clearTimeout(phase2Timer);
      clearTimeout(completeTimer);
    };
  }, []);

  // Get current loading message based on phase
  // NOTE: "trucks" is intentional UI display text - do not replace with "units" in terminology sweeps
  const getLoadingMessage = () => {
    if (loadingPhase === 0) return 'Loading map...';
    if (loadingPhase === 1) return 'Loading trucks...';
    return quirkyMessage;
  };

  // ISS-136: nothing bounded the overlay's wait on Mapbox, the units fetch and
  // sign-in, so a stall held "Phoning home…" forever. After the ceiling the
  // overlay drops; if the map still isn't ready, MapSlowNotice offers a reload.
  const [loadingCeilingReached, setLoadingCeilingReached] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoadingCeilingReached(true), LOADING_CEILING_MS);
    return () => clearTimeout(timer);
  }, []);

  // Show loading when either data or map is still loading OR minimum time hasn't passed
  const dataOrMapPending = (loading && units.length === 0) || !mapLoaded;
  const isLoading = (dataOrMapPending || !minLoadingComplete) && !loadingCeilingReached;
  // Either one still pending at the ceiling gets the notice: a stalled units
  // request under a ready map would otherwise leave an empty map (Seer #955).
  const mapSlow = loadingCeilingReached && dataOrMapPending;
  const loadingMessage = getLoadingMessage();

  // Handle map loaded callback
  const handleMapLoaded = () => setMapLoaded(true);

  // Error is now displayed in sidebar, not as blocking screen

  // Mobile layout
  if (isMobile) {
    return (
      <div className="h-dvh flex flex-col bg-background">
        <DashboardLayout
          units={sidebarUnits}
          allUnits={units}
          selectedUnit={selectedUnit}
          hoveredUnit={hoveredUnit}
          onSelectUnit={handleSelectUnit}
          onHoverUnit={handleSidebarHover}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          statusFilters={statusFilters}
          onStatusFiltersChange={handleStatusFiltersChange}
          showAlerts={showAlerts}
          onShowAlertsChange={handleShowAlertsChange}
          onClearFilters={handleClearFilters}
          sidebarRef={sidebarRef}
          isMobile={true}
          error={error}
          onSearchOpen={() => setIsSearchOpen(true)}
          searchOpen={isSearchOpen}
          onSearchOpenChange={setIsSearchOpen}
          contentLoaded={!isLoading}
        >
          {/* Always render MapView for consistent hook count, toggle visibility with CSS */}
          <div
            className={mobileTab === 'map' ? 'absolute inset-0' : 'absolute inset-0 invisible pointer-events-none'}
            aria-hidden={mobileTab !== 'map'}
          >
            <MapView
                  units={filteredUnits}
                  selectedUnit={selectedUnit}
                  hoveredUnit={hoveredUnit}
                  onUnitClick={handleMarkerClick}
                  onUnitHover={handleMapHover}
                  onViewportChange={handleViewportChange}
                  viewportFilterEnabled={viewportFilterEnabled}
                  onViewportFilterChange={setViewportFilterEnabled}
                  isSidebarCollapsed={false} // Mobile doesn't have sidebar
                  onMapLoaded={handleMapLoaded}
                />
          </div>
          {/* Always render MobileUnitList for consistent hook count - use CSS visibility */}
          <div
            className={mobileTab === 'list'
              ? 'absolute inset-0 overflow-y-auto'
              : 'absolute inset-0 invisible pointer-events-none'}
            aria-hidden={mobileTab !== 'list'}
          >
            <MobileUnitList
              units={sidebarUnits}
              totalUnits={filteredUnits.length}
              selectedUnit={selectedUnit}
              onSelectUnit={handleSelectUnit}
            />
          </div>
          {/* Always render UnitDetailView for consistent hook count - use CSS visibility */}
          <div
            className={mobileTab === 'detail' && selectedUnit
              ? 'absolute inset-0 overflow-y-auto'
              : 'absolute inset-0 invisible pointer-events-none'}
            aria-hidden={mobileTab !== 'detail' || !selectedUnit}
          >
            <UnitDetailView
              unit={detailUnitForView ?? PLACEHOLDER_UNIT}
              onBack={() => setMobileTab(previousMobileTab)}
              onSystemUpdate={(system, settings) =>
                selectedUnit && handleSystemUpdate(selectedUnit.id, system, settings)
              }
              onTabChange={(tab) => {
                // Store the tab change for the current unit
                if (selectedUnit) {
                  setUnitTabs(prev => {
                    const newMap = new Map(prev);
                    newMap.set(selectedUnit.id, tab);
                    return newMap;
                  });
                }
              }}
              initialTab={selectedUnit ? (unitTabs.get(selectedUnit.id) || 'solar') : 'solar'}
              scrollToTabs={scrollToTabs}
            />
          </div>
        </DashboardLayout>

        {/* Mobile Error Banner - fixed position above tab navigation to avoid layout overflow */}
        {error && (
          <div
            className="fixed left-0 right-0 bottom-16 z-50 bg-destructive/10 border-t border-destructive/20 px-4 py-2"
            role="alert"
            aria-live="polite"
          >
            <button
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              className="w-full flex items-center justify-between text-destructive text-sm"
              aria-expanded={showErrorDetails}
              aria-controls="mobile-error-details"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                <span>Error loading data</span>
              </div>
              {showErrorDetails ? (
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            {showErrorDetails && (
              <div
                id="mobile-error-details"
                className="mt-2 p-2 bg-destructive/5 rounded text-xs text-destructive/80 font-mono break-all"
              >
                {error}
              </div>
            )}
          </div>
        )}

        <MobileTabNavigation
          activeTab={mobileTab}
          onTabChange={handleMobileTabChange}
          hasSelectedUnit={!!selectedUnit}
          isAdmin={isAdmin}
        />

        {/* Unified loading overlay with smooth fade-out (mobile) */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              key="loading-overlay"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
            >
              <LottieLoading message={loadingMessage} fullScreen={false} />
            </motion.div>
          )}
        </AnimatePresence>
        {mapSlow && <MapSlowNotice />}
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="relative h-dvh w-screen">
      <DashboardLayout
        units={sidebarUnits}
        allUnits={units}
        selectedUnit={selectedUnit}
        hoveredUnit={hoveredUnit}
        onSelectUnit={handleSelectUnit}
        onHoverUnit={handleSidebarHover}
        onRefresh={loadUnits}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilters={statusFilters}
        onStatusFiltersChange={handleStatusFiltersChange}
        showAlerts={showAlerts}
        onShowAlertsChange={setShowAlerts}
        onClearFilters={handleClearFilters}
        sidebarRef={sidebarRef}
        isSidebarCollapsed={isSidebarCollapsed}
        onSidebarCollapsedChange={setIsSidebarCollapsed}
        error={error}
        contentLoaded={!isLoading}
        onResetViewportFilter={handleRestoreView}
      >
        {/* Always render map */}
        <MapView
          units={filteredUnits}
          selectedUnit={selectedUnit}
          hoveredUnit={hoveredUnit}
          onUnitClick={handleMarkerClick}
          onUnitHover={handleMapHover}
          onViewportChange={handleViewportChange}
          viewportFilterEnabled={viewportFilterEnabled}
          onViewportFilterChange={setViewportFilterEnabled}
          isSidebarCollapsed={isSidebarCollapsed}
          onMapLoaded={handleMapLoaded}
        />
      </DashboardLayout>

      {/* Overlay detail view when unit is selected - positioned relative to viewport */}
      <AnimatePresence>
        {selectedUnit && viewMode === 'detail' && (
          <div
            className={`fixed top-[104px] right-0 bottom-0 z-20 bg-background pointer-events-auto ${
              isSidebarCollapsed ? 'left-[100px]' : 'left-[360px]'
            }`}
            onClick={handleBackToMap}
          >
            {/* Detail panel fills full user viewport (viewport minus sidebar) */}
            <div className="h-full w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <UnitDetailView
                key="detail"
                unit={detailUnitForView ?? selectedUnit}
                onBack={handleBackToMap}
                onOpenGallery={handleOpenGallery}
                onSystemUpdate={(system, settings) =>
                  handleSystemUpdate(selectedUnit.id, system, settings)
                }
                onTabChange={(tab) => {
                  // Store the tab change for the current unit
                  if (selectedUnit) {
                    setUnitTabs(prev => {
                      const newMap = new Map(prev);
                      newMap.set(selectedUnit.id, tab);
                      return newMap;
                    });
                  }
                }}
                initialTab={selectedUnit ? (unitTabs.get(selectedUnit.id) || 'solar') : 'solar'}
                scrollToTabs={scrollToTabs}
              />
            </div>
          </div>
        )}

        {/* Overlay gallery view when gallery mode is active - positioned relative to viewport */}
        {selectedUnit && viewMode === 'gallery' && (
          <div
            className={`fixed top-[104px] right-0 bottom-0 z-20 bg-black/40 pointer-events-auto ${
              isSidebarCollapsed ? 'left-[100px]' : 'left-[360px]'
            }`}
            onClick={handleBackToMap}
          >
            {/* Gallery panel fills full user viewport (viewport minus sidebar) */}
            <div className="h-full w-full" onClick={(e) => e.stopPropagation()}>
              <GalleryPage
                key="gallery"
                unitId={selectedUnit.id}
                onBack={handleBackToMap}
              />
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Unified loading overlay with smooth fade-out */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            key="loading-overlay"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
          >
            <LottieLoading message={loadingMessage} fullScreen={false} />
          </motion.div>
        )}
      </AnimatePresence>
      {mapSlow && <MapSlowNotice />}
    </div>
  );
}